import type { SceneObject } from '../../core/SceneObject';
import type { GraphData } from '../../scenedata/behaviors/behaviorgraphdata';
import type { PagesData } from '../../scenedata/behaviors/pagesdata';
import type { BehaviorData } from '../../scenedata/behaviordata';
import type { UndoCommand } from '../undo/UndoCommand';
import { BehaviorCommitCommand } from '../undo/BehaviorCommitCommand';
import { dispatchBehaviorChanged } from '../undo/editor-events';
import { Logger } from '../../utils/Logger';
import {
  rewriteStateRefsForTargets,
  graphReferencesStateName,
  type StateRefTargetPredicate,
} from './stateReferenceRewrite';
import { groupDescendants } from './groupBoundaries';

// ---------------------------------------------------------------------------
// State-name reference healing — the SCENE-WIDE, target-aware pass that keeps
// interaction refs and Pages state maps pointing at a state after it is renamed
// or deleted.
//
// Runtime state references are addressed by NAME. Unlike object PATHS (which
// break passively on any topology mutation → the identity registry), state
// names only break ACTIVELY, at rename/delete time — so a transactional scan at
// mutation time is sufficient; no persistent tracking is needed. This is the
// single place that scan lives (previously split between a subtree-scoped
// blanket rewrite in statesActions and a TF-only cross-object pass).
//
// The heal EXECUTES the graph/Pages commits and RETURNS the resulting undo
// commands WITHOUT pushing them (same "return command, caller bundles" pattern
// as rigContainer.ts), so the caller folds them into the SAME rename/delete undo
// entry — one Ctrl+Z restores Properties + graphs + pages together.
// ---------------------------------------------------------------------------

// Structural shape of the BehaviorGraph behavior — only what the heal needs. We
// DELIBERATELY do not import the BehaviorGraph class: the string lookup + this
// local interface keep this editor module decoupled from the behavior impl (same
// pattern as referenceIdentity.ts). This is the structural-decoupling case, NOT
// the SceneObject cycle exception — there's no value import to break.
interface BehaviorGraphLike {
  getData?: () => { graph?: GraphData } | undefined;
  commit?: (patch: { graph: GraphData }) => Promise<unknown>;
}

// Structural shape of the Pages behavior — only the per-page state assignments
// are touched.
interface PagesLike {
  getData?: () => Partial<PagesData> | undefined;
  commit?: (patch: { pages: PagesData['pages'] }) => Promise<unknown>;
}

const pathOf = (o: SceneObject): string => o.getPath?.() ?? o.name;

/**
 * Build (but do NOT push) the undo commands that heal every reference to state
 * `from`→`to` (`to === ''` = delete redirect) on `owner`'s subtree, scanning the
 * WHOLE scene:
 *
 *  - `trace/setState` inline names and cycle-graph `math/eq` constants, on every
 *    object whose graph targets a node inside `owner`'s GROUP subtree (target-aware
 *    — an unrelated object with its own same-named state is never touched);
 *  - Pages `pages[].states` assignments whose target resolves into the subtree
 *    (rename → rewrite the state name; delete → REMOVE the assignment so the
 *    target reverts to base, since pages.ts passes the raw value to setState
 *    with no `''`→revert mapping).
 *
 * BOUNDARY-SCOPED: the subtree is `groupDescendants(owner)` — a nested group
 * boundary and everything under it are PRUNED. This mirrors the rename/delete
 * fan-out (which also stops at boundaries): a ref that targets an object inside an
 * INNER group points at that inner group's same-named state, so the outer group's
 * rename/delete must not rewrite it.
 *
 * Each changed graph/Pages behavior is committed and captured as one
 * `BehaviorCommitCommand`. Best-effort per object: a failure is logged and
 * skipped, never thrown. The caller pushes the returned commands as part of its
 * rename/delete bundle.
 */
export async function buildStateRefHealCommands(
  owner: SceneObject,
  from: string,
  to: string,
): Promise<UndoCommand[]> {
  const traceScene = owner.traceScene;
  const commands: UndoCommand[] = [];
  if (!traceScene?.getSceneObjects || !traceScene.resolveSceneObject) return commands;

  // Match by canonical path (not object identity) — resolveSceneObject may hand
  // back a different SceneObject instance than `owner` for the same logical node.
  // GROUP subtree only (groupDescendants prunes nested boundaries), so a ref into
  // an inner group's same-named state is left alone by the outer rename/delete.
  const subtreePaths = new Set<string>(
    [owner, ...groupDescendants(owner)].map(pathOf),
  );

  // Predicate over a state-ref node's inline target, resolved against its holder.
  // A MISSING target (undefined) means "self" — the holder itself. `$`-keyword
  // and relative/absolute paths are resolved by resolveSceneObject.
  const targetInScopeFor =
    (holder: SceneObject): StateRefTargetPredicate =>
    (targetPath: string | undefined): boolean => {
      if (targetPath === undefined) return subtreePaths.has(pathOf(holder)); // self
      const resolved = traceScene.resolveSceneObject?.(targetPath, holder) ?? null;
      return resolved != null && subtreePaths.has(pathOf(resolved));
    };

  for (const obj of traceScene.getSceneObjects()) {
    try {
      await healGraph(obj, from, to, targetInScopeFor(obj), commands);
      await healPages(obj, from, to, targetInScopeFor(obj), commands);
    } catch (err) {
      Logger.warn(
        `[stateRefHeal] failed healing '${from}'→'${to || '<base>'}' on ${obj.name}`,
        err,
      );
    }
  }
  return commands;
}

async function healGraph(
  obj: SceneObject,
  from: string,
  to: string,
  targetInScope: StateRefTargetPredicate,
  out: UndoCommand[],
): Promise<void> {
  // string form is intentional — see BehaviorGraphLike note above.
  const bg = obj.getBehavior('BehaviorGraph') as BehaviorGraphLike | null;
  const graph = bg?.getData?.()?.graph;
  if (!graph || !bg?.commit) return;

  // Cheap read-only pre-check before the deep clone: most graphs on any given
  // rename don't reference `from` at all, so skip the structuredClone for them.
  if (!graphReferencesStateName(graph, from)) return;

  // Clone so the pre-edit graph is the undo `before` — an in-place rewrite would
  // make before === after (a no-op undo).
  const nextGraph = structuredClone(graph);
  if (!rewriteStateRefsForTargets(nextGraph, from, to, targetInScope)) return;

  const before = (bg.getData?.() ?? {}) as BehaviorData;
  const errors = await bg.commit({ graph: nextGraph });
  if (Array.isArray(errors) && errors.length > 0) {
    Logger.warn(`[stateRefHeal] BehaviorGraph commit on ${obj.name} rejected`, errors);
    return;
  }
  const after = (bg.getData?.() ?? before) as BehaviorData;
  out.push(new BehaviorCommitCommand(obj, 'BehaviorGraph', before, after, null));
  dispatchBehaviorChanged({ objectPath: obj.getPath(), behaviorType: 'BehaviorGraph', action: 'updated' });
}

async function healPages(
  obj: SceneObject,
  from: string,
  to: string,
  targetInScope: StateRefTargetPredicate,
  out: UndoCommand[],
): Promise<void> {
  const pages = obj.getBehavior('Pages') as PagesLike | null;
  const pageList = pages?.getData?.()?.pages;
  if (!pageList || pageList.length === 0 || !pages?.commit) return;

  // Assignment targets are relative to the Pages HOST object (same context
  // pages.ts uses to resolve them at runtime).
  const inScope = (target: string): boolean => targetInScope(target);

  let changed = false;
  const next: NonNullable<PagesData['pages']> = pageList.map(page => {
    const states = page?.states;
    if (!states || states.length === 0) return { ...page };
    const nextStates = [];
    for (const a of states) {
      if (a.state === from && inScope(a.target)) {
        changed = true;
        if (to === '') continue; // delete → drop the assignment (target reverts to base)
        nextStates.push({ ...a, state: to });
      } else {
        nextStates.push({ ...a });
      }
    }
    // Omit an emptied-out states array (a delete that removed its only
    // assignment) — the target reverts to base via pages.ts's unassigned-path
    // branch either way. The page entry itself stays (positional alignment).
    return nextStates.length > 0 ? { ...page, states: nextStates } : { ...page, states: undefined };
  });
  if (!changed) return;

  const before = (pages.getData?.() ?? {}) as BehaviorData;
  const errors = await pages.commit({ pages: next });
  if (Array.isArray(errors) && errors.length > 0) {
    Logger.warn(`[stateRefHeal] Pages commit on ${obj.name} rejected`, errors);
    return;
  }
  const after = (pages.getData?.() ?? before) as BehaviorData;
  out.push(new BehaviorCommitCommand(obj, 'Pages', before, after, null));
  dispatchBehaviorChanged({ objectPath: obj.getPath(), behaviorType: 'Pages', action: 'updated' });
}
